'use client';

import type {PropsWithChildren} from 'react';
import {createContext, useContext, useState} from 'react';

import type {FieldOfArea} from '../../sanity/complaint-guide/constants';
import type {ComplaintGuideQuestion} from '../complaint-guide';

export type GuideAnswer = {
	_key: string;
	_type: 'negativeAnswer' | 'positiveAnswer';
	title: string;
	questionKey: string;
	questionTitle: string;
	positiveAnswerId?: string;
	questionId: string;
	fieldOfArea?: FieldOfArea;
};

type ComplaintGuideContextValue = {
	currentStep: number;
	questions: ComplaintGuideQuestion[];
	setQuestions: (questions: ComplaintGuideQuestion[]) => void;
	currentQuestion: ComplaintGuideQuestion | null;
	answers: GuideAnswer[];
	currentAnswer: GuideAnswer | null;
	setAnswer: (answer: GuideAnswer) => void;
	setAnswers: (answers: GuideAnswer[]) => void;
	goToNextStep: () => void;
	goToPreviousStep: () => void;
	showResult: boolean;
	setShowResult: (showResult: boolean) => void;
	isLastQuestion: boolean;
	activeFieldOfArea: FieldOfArea | null;
};

const ComplaintGuideContext = createContext<ComplaintGuideContextValue>({
	currentStep: 0,
	questions: [],
	setQuestions: () => {},
	currentQuestion: null,
	answers: [],
	currentAnswer: null,
	setAnswer: () => {},
	setAnswers: () => {},
	goToNextStep: () => {},
	goToPreviousStep: () => {},
	showResult: false,
	setShowResult: () => {},
	isLastQuestion: false,
	activeFieldOfArea: null,
});

export const ComplaintGuideProvider = ({children}: PropsWithChildren) => {
	const [questions, setQuestions] = useState<ComplaintGuideQuestion[]>([]);
	const [answers, setAnswers] = useState<GuideAnswer[]>([]);
	const [currentStep, setCurrentStep] = useState(0);
	const [showResult, setShowResult] = useState(false);

	// Get the current question based on the current step
	const currentQuestion = questions[currentStep] || null;

	// The active field of area is derived from the selected answers: the
	// marker lives on a single question, so at most one stored answer carries
	// it. Deriving it keeps it in sync automatically (no manual reset needed).
	const activeFieldOfArea =
		answers.find((a) => a.fieldOfArea)?.fieldOfArea ?? null;

	// Save the answer for the current question
	const setAnswer = (answer: GuideAnswer) => {
		setAnswers((prev) => [
			...prev.filter((a) => a.questionKey !== answer.questionKey),
			answer,
		]);
	};

	// Move to the next step if the answer is positive
	const goToNextStep = () => {
		const currentAnswer = answers?.find(
			(a) => a.questionKey === currentQuestion?._key,
		);
		if (
			currentAnswer &&
			currentAnswer._type === 'positiveAnswer' &&
			currentStep < questions.length - 1
		) {
			setCurrentStep((prev) => prev + 1);
		}
	};

	// Move to the previous step
	const goToPreviousStep = () => {
		if (currentStep > 0) {
			setCurrentStep((prev) => prev - 1);
		}
	};

	return (
		<ComplaintGuideContext.Provider
			value={{
				currentStep,
				questions,
				setQuestions,
				currentQuestion,
				answers,
				setAnswer,
				setAnswers,
				goToNextStep,
				goToPreviousStep,
				currentAnswer:
					answers?.find(
						(a) => a.questionKey === currentQuestion?._key,
					) || null,
				isLastQuestion: currentStep === questions.length - 1,
				showResult,
				setShowResult,
				activeFieldOfArea,
			}}
		>
			{children}
		</ComplaintGuideContext.Provider>
	);
};

export const useComplaintGuide = () => {
	const context = useContext(ComplaintGuideContext);
	if (!context) {
		throw new Error(
			'useComplaintGuide must be used within a ComplaintGuideProvider',
		);
	}
	return context;
};
